import { useCallback, useContext, useRef } from 'react'
import {
    reportCheckinTimeSet,
    setTimestampDisplay,
    type TimestampDisplayState,
} from '../api/client'
import { UserContext } from '../components/UserContext'
import {
    TIMESTAMP_DISPLAY_MODES,
    type AppliesPrecision,
    type TimestampDisplay,
} from '../utils/dates'

/**
 * The timestamp display mode, from wherever it is set.
 *
 * There are three entry points to one setting — the account page, the
 * check-ins page's sort menu, and tapping a date label on the timeline — and
 * they all write the same column. The account page is where someone goes
 * looking for a preference; the other two are where the setting is *felt*,
 * which is the half that was missing: the feature shipped invisible from the
 * page it applies to.
 *
 * One hook rather than three copies of the write, because the part that is
 * easy to get wrong is not the request. It is that the mode rides on the user
 * object rather than having its own `GET` — a deliberate earlier call, so the
 * timeline is never painted once in the wrong mode — which means **every**
 * write has to put the answer back on the cached user or nothing changes
 * until a reload.
 */

/** The human words for the three modes, shared by the account page and the
 *  sort menu. One vocabulary, met in two places. */
export const TIMESTAMP_DISPLAY_LABELS: Record<TimestampDisplay, string> = {
    none: 'Not at all',
    approximate: 'Roughly',
    precise: 'Exactly',
}

/** The next mode a tap on a date label moves to: none → approximate →
 *  precise → none. Derived from `TIMESTAMP_DISPLAY_MODES` so the cycle cannot
 *  drift from the list the menu renders. */
export function nextTimestampDisplay(mode: TimestampDisplay): TimestampDisplay {
    const at = TIMESTAMP_DISPLAY_MODES.indexOf(mode)
    return TIMESTAMP_DISPLAY_MODES[(at + 1) % TIMESTAMP_DISPLAY_MODES.length]
}

export interface UseTimestampDisplay {
    mode: TimestampDisplay
    /** Whether the user has ever chosen, which is what gates the auto-set. */
    chosen: boolean
    /** An explicit choice. Optimistic, rolled back if the write fails. */
    choose: (mode: TimestampDisplay) => Promise<void>
    /** An explicit choice of whatever comes next in the cycle. */
    cycle: () => Promise<void>
    /** Report that the user just set a time on a checkin, and how. Silent, and
     *  a no-op once they have chosen. */
    noteCheckinTimeSet: (precision: AppliesPrecision) => Promise<void>
}

export function useTimestampDisplay(): UseTimestampDisplay {
    const { user, setUser } = useContext(UserContext)
    const mode: TimestampDisplay = user?.timestampDisplay ?? 'none'
    const chosen = user?.timestampDisplayChosen ?? false

    // The `<input type="time">` can fire more than one change for one edit —
    // stepping the hour, then the minute — and `chosen` only turns true once
    // the first response lands. A ref rather than the flag alone, so the burst
    // costs one request instead of three.
    const autoSetAttempted = useRef(false)

    const applyState = useCallback(
        (state: TimestampDisplayState) => {
            setUser(current =>
                current
                    ? {
                          ...current,
                          timestampDisplay: state.mode,
                          timestampDisplayChosen: state.chosen,
                      }
                    : current,
            )
        },
        [setUser],
    )

    const choose = useCallback(
        async (next: TimestampDisplay) => {
            if (!user) return
            const previous = { mode, chosen }
            // Optimistic, so the timeline changes as the option is picked
            // rather than a round trip later. Rolled back on failure — a
            // preference that silently didn't stick is worse than one that
            // visibly bounced.
            applyState({ mode: next, chosen: true })
            autoSetAttempted.current = true
            try {
                applyState(await setTimestampDisplay(next))
            } catch {
                applyState(previous)
            }
        },
        [applyState, chosen, mode, user],
    )

    const cycle = useCallback(() => choose(nextTimestampDisplay(mode)), [choose, mode])

    const noteCheckinTimeSet = useCallback(
        async (precision: AppliesPrecision) => {
            if (!user || chosen || autoSetAttempted.current) return
            autoSetAttempted.current = true
            try {
                // Not optimistic: the client does not decide what a precision
                // means, and guessing would show a mode the server may decline
                // to write. The server answers with the effective state, which
                // is the only thing that can update the cached user — there is
                // no GET for this.
                applyState(await reportCheckinTimeSet(precision))
            } catch {
                // Silent by design (no toast either way), and a failure here
                // must not disturb the save the user actually asked for. The
                // gate reopens so the next time they set a time it tries
                // again.
                autoSetAttempted.current = false
            }
        },
        [applyState, chosen, user],
    )

    return { mode, chosen, choose, cycle, noteCheckinTimeSet }
}
